/**
 * @fileoverview Admin Dashboard - Auth Hook
 * @description Authentication state management for admin users
 */
import { useState, useCallback, useEffect } from 'react';
import { login as apiLogin, setToken, getToken, clearToken, silentRefresh, AUTH_EXPIRED_EVENT } from '../services/api';
import { clearAlcoholThresholdsCache } from '../utils/alcoholThresholds';

export interface AdminUser {
  id: string;
  user_name: string;
  user_role: string;
  company_id: string;
  /** app-superadmin から発行された仮想運営管理者セッションの場合 true */
  is_super_admin?: boolean;
}

export const ADMIN_ROLES = ['system_admin', 'shore_admin'] as const;
export function isAdminRole(role: string): boolean {
  return (ADMIN_ROLES as readonly string[]).includes(role);
}

/** JWT のペイロード部を（署名検証なしで）デコードする。表示用途のみ。 */
function decodeJwtPayload(t: string): Record<string, unknown> {
  const seg = t.split('.')[1] ?? '';
  const b64 = seg.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const json = decodeURIComponent(
    bin.split('').map(c => '%' + c.charCodeAt(0).toString(16).padStart(2, '0')).join('')
  );
  return JSON.parse(json) as Record<string, unknown>;
}

export function useAuth() {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      // 運営管理者モード: app-superadmin から #sa=<jwt> で遷移してきた場合、
      // トークンを取り込んで仮想運営管理者としてログイン状態にする。
      const m = window.location.hash.match(/[#&]sa=([^&]+)/);
      if (m && m[1]) {
        try {
          const accessToken = decodeURIComponent(m[1]);
          const payload = decodeJwtPayload(accessToken);
          setToken(accessToken);
          const u: AdminUser = {
            id: String(payload['user_id'] ?? ''),
            user_name: String(payload['user_name'] ?? '運営管理者'),
            user_role: String(payload['user_role'] ?? 'system_admin'),
            company_id: String(payload['company_id'] ?? ''),
            is_super_admin: payload['is_super_admin'] === true,
          };
          localStorage.setItem('admin_user', JSON.stringify(u));
          // URL からトークンを除去（履歴に残さない）
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
          setUser(u);
          setLoading(false);
          return;
        } catch { /* 不正なトークンは通常フローへフォールバック */ }
      }

      const stored = localStorage.getItem('admin_user');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (!isAdminRole(parsed?.user_role ?? '')) {
            clearToken();
            localStorage.removeItem('admin_user');
            setLoading(false);
            return;
          }
          // sessionStorage にトークンが残っていればそのまま使用（ページリロード対応）
          if (!getToken()) {
            // トークンがない場合のみ cookie 経由でサイレントリフレッシュ
            const ok = await silentRefresh();
            if (!ok) {
              clearToken();
              localStorage.removeItem('admin_user');
              setLoading(false);
              return;
            }
          }
          setUser(parsed);
        } catch {
          clearToken();
          localStorage.removeItem('admin_user');
        }
      }
      setLoading(false);
    };
    init();
  }, []);

  // API 側でセッション切れを検知したら（リフレッシュ失敗）、ユーザー状態を落としてログイン画面へ戻す。
  // これが無いと、ログアウト済みのタブがサイドバーのポーリングで 401→refresh 400 を1分に何度も繰り返し続ける。
  useEffect(() => {
    const onExpired = () => setUser(u => (u ? null : u));
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  const doLogin = useCallback(async (identifier: string, password: string) => {
    const res = await apiLogin(identifier, password);
    if ('mfa_required' in res) {
      // MFA が必要な場合は呼び出し元で処理する
      return res;
    }
    setToken(res.data.access_token);
    const u = res.data.user as unknown as AdminUser;
    setUser(u);
    localStorage.setItem('admin_user', JSON.stringify(u));
    return res;
  }, []);

  const loginWithToken = useCallback((accessToken: string, userData: Record<string, unknown>) => {
    setToken(accessToken);
    const u = userData as unknown as AdminUser;
    setUser(u);
    localStorage.setItem('admin_user', JSON.stringify(u));
  }, []);

  const doLogout = useCallback(() => {
    clearToken();
    // 同じタブで別会社の管理者がログインしたとき、前の会社の閾値を見せない
    clearAlcoholThresholdsCache();
    setUser(null);
  }, []);

  return { user, loading, login: doLogin, loginWithToken, logout: doLogout, isAuthenticated: !!user };
}
